/*
 * Handwriting Preview for Shopify
 * --------------------------------
 * Customer uploads a photo of their handwriting -> paper background removed
 * in the browser -> ink recoloured (white by default) -> overlaid on the
 * product image, draggable / resizable / rotatable -> on "Confirm", the
 * transparent PNG and a preview composite are uploaded (Cloudinary, unsigned
 * preset) and their URLs are attached to the cart line as line item properties.
 *
 * No libraries, no server. Works on any element with [data-hw-preview].
 */
(function () {
  'use strict';

  var MAX_DIM = 1600;      // longest edge the photo is processed at
  var SOFTNESS = 30;       // width of the alpha ramp (0-255 luminance units)
  var BBOX_ALPHA = 40;     // alpha above which a pixel counts as ink for auto-crop
  var PAD = 8;             // px of padding around the auto-cropped ink
  var DEFAULT_SENSITIVITY = 100; // used when there is no sensitivity slider (0-100; lower = fewer specks)

  /* ---------- image processing ---------- */

  function loadImage(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('Could not read that image.')); };
      img.src = url;
    });
  }

  // Draws the photo (downscaled) and returns luminance + local background estimate.
  function analyse(img) {
    var scale = Math.min(1, MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight));
    var w = Math.max(1, Math.round(img.naturalWidth * scale));
    var h = Math.max(1, Math.round(img.naturalHeight * scale));
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    var px = ctx.getImageData(0, 0, w, h).data;

    var gray = new Float32Array(w * h);
    for (var i = 0, j = 0; i < gray.length; i++, j += 4) {
      gray[i] = 0.299 * px[j] + 0.587 * px[j + 1] + 0.114 * px[j + 2];
    }
    // Local mean = estimate of paper brightness at each pixel.
    // This is what copes with shadows / uneven lighting on phone photos.
    var r = Math.max(12, Math.round(Math.max(w, h) / 25));
    var bg = boxMean(gray, w, h, r);
    // Second pass: ignore dark (ink) pixels so thick strokes don't drag the
    // background estimate down and get partially erased.
    var cleaned = new Float32Array(w * h);
    for (var k = 0; k < cleaned.length; k++) cleaned[k] = gray[k] < bg[k] - 15 ? bg[k] : gray[k];
    bg = boxMean(cleaned, w, h, r);

    return { w: w, h: h, gray: gray, bg: bg };
  }

  function boxMean(src, w, h, r) {
    var W = w + 1;
    var integ = new Float64Array(W * (h + 1));
    for (var y = 0; y < h; y++) {
      var row = 0;
      for (var x = 0; x < w; x++) {
        row += src[y * w + x];
        integ[(y + 1) * W + x + 1] = integ[y * W + x + 1] + row;
      }
    }
    var out = new Float32Array(w * h);
    for (var yy = 0; yy < h; yy++) {
      var y0 = Math.max(0, yy - r), y1 = Math.min(h, yy + r + 1);
      for (var xx = 0; xx < w; xx++) {
        var x0 = Math.max(0, xx - r), x1 = Math.min(w, xx + r + 1);
        var s = integ[y1 * W + x1] - integ[y0 * W + x1] - integ[y1 * W + x0] + integ[y0 * W + x0];
        out[yy * w + xx] = s / ((y1 - y0) * (x1 - x0));
      }
    }
    return out;
  }

  /* Separates the writing from things that aren't writing: paper edges, table
   * shadows, specks. Finds each connected blob of ink, throws away blobs that touch
   * the photo's edge, giant thin outlines (a paper edge), and small specks lying away
   * from the writing; erases them from `alpha` and returns the writing's bounding box.
   * Small marks close to the writing (i-dots, full stops, kisses) are kept. */
  function keepWriting(alpha, w, h) {
    var n = w * h, lab = new Int32Array(n), stack = new Int32Array(n), comps = [];
    for (var s = 0; s < n; s++) {
      if (lab[s] || alpha[s] <= BBOX_ALPHA) continue;
      var id = comps.length + 1, top = 0, c = { area: 0, minX: w, minY: h, maxX: -1, maxY: -1, border: false };
      lab[s] = id; stack[top++] = s;
      while (top) {
        var p = stack[--top], x = p % w, y = (p / w) | 0;
        c.area++;
        if (x < c.minX) c.minX = x; if (x > c.maxX) c.maxX = x;
        if (y < c.minY) c.minY = y; if (y > c.maxY) c.maxY = y;
        if (x === 0 || y === 0 || x === w - 1 || y === h - 1) c.border = true;
        for (var dy = -1; dy <= 1; dy++) {
          var yy = y + dy; if (yy < 0 || yy >= h) continue;
          for (var dx = -1; dx <= 1; dx++) {
            var xx = x + dx; if (xx < 0 || xx >= w) continue;
            var q = yy * w + xx;
            if (!lab[q] && alpha[q] > BBOX_ALPHA) { lab[q] = id; stack[top++] = q; }
          }
        }
      }
      comps.push(c);
    }
    if (!comps.length) return null;

    comps.forEach(function (c) {
      var bw = c.maxX - c.minX + 1, bh = c.maxY - c.minY + 1;
      var outline = bw > w * 0.6 && bh > h * 0.6 && c.area / (bw * bh) < 0.03;   // paper edge / frame line
      c.junk = c.border || outline;
    });
    var good = comps.filter(function (c) { return !c.junk; });
    if (!good.length) return null;
    var biggest = Math.max.apply(null, good.map(function (c) { return c.area; }));
    var minArea = Math.max(12, biggest * 0.02);

    // Box around the substantial strokes, then admit small marks near it.
    var B = { minX: w, minY: h, maxX: -1, maxY: -1 };
    function grow(c) { B.minX = Math.min(B.minX, c.minX); B.minY = Math.min(B.minY, c.minY); B.maxX = Math.max(B.maxX, c.maxX); B.maxY = Math.max(B.maxY, c.maxY); }
    good.forEach(function (c) { if (c.area >= minArea) { c.keep = true; grow(c); } });
    var mx = (B.maxX - B.minX) * 0.06 + 10, my = (B.maxY - B.minY) * 0.25 + 10;
    good.forEach(function (c) {
      if (!c.keep && c.minX >= B.minX - mx && c.maxX <= B.maxX + mx && c.minY >= B.minY - my && c.maxY <= B.maxY + my) c.keep = true;
    });
    B = { minX: w, minY: h, maxX: -1, maxY: -1 };
    good.forEach(function (c) { if (c.keep) grow(c); });

    // Erase everything that isn't kept writing (including faint haze outside the box).
    for (var i = 0; i < n; i++) {
      if (!alpha[i]) continue;
      var l = lab[i];
      if (l ? !comps[l - 1].keep : true) {
        var px = i % w, py = (i / w) | 0;
        if (l || px < B.minX || px > B.maxX || py < B.minY || py > B.maxY) alpha[i] = 0;
      }
    }
    return B;
  }

  // Builds a cropped, transparent canvas containing only the ink in `hex` colour.
  function renderInk(a, sensitivity, hex) {
    var T = 45 - 0.4 * sensitivity;            // sensitivity 0..100 -> threshold 45..5
    var rgb = hexToRgb(hex);
    var n = a.w * a.h;
    var alpha = new Uint8ClampedArray(n);

    for (var i = 0; i < n; i++) {
      var v = (a.bg[i] - a.gray[i] - T) / SOFTNESS;
      v = v < 0 ? 0 : v > 1 ? 1 : v;
      alpha[i] = Math.round(v * 255);
    }

    var box = keepWriting(alpha, a.w, a.h);
    if (!box) return null;
    var minX = box.minX, minY = box.minY, maxX = box.maxX, maxY = box.maxY;

    minX = Math.max(0, minX - PAD); minY = Math.max(0, minY - PAD);
    maxX = Math.min(a.w - 1, maxX + PAD); maxY = Math.min(a.h - 1, maxY + PAD);
    var cw = maxX - minX + 1, ch = maxY - minY + 1;

    var c = document.createElement('canvas');
    c.width = cw; c.height = ch;
    var ctx = c.getContext('2d');
    var out = ctx.createImageData(cw, ch);
    var d = out.data;
    for (var yy = 0; yy < ch; yy++) {
      for (var xx = 0; xx < cw; xx++) {
        var src = (yy + minY) * a.w + (xx + minX);
        var o = (yy * cw + xx) * 4;
        d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = alpha[src];
      }
    }
    ctx.putImageData(out, 0, 0);
    return c;
  }

  /* ---------- frame detection ----------
   * Finds the coloured frame in a product photo: scans inward from each edge of
   * the image until the colour changes from the background (outer edge of the
   * frame), then keeps going until it changes from the frame colour for a solid
   * run (inner edge, where the mount starts). Several scan lines per side, median
   * taken, so the sample writing on the frame doesn't throw it off.
   * Returns { outer, inner } as fractions of the image (l, t, r, b), or null.
   */
  function detectFrame(img) {
    var nw = img.naturalWidth, nh = img.naturalHeight;
    if (!nw || !nh) return null;
    var s = Math.min(1, 500 / Math.max(nw, nh));
    var w = Math.max(1, Math.round(nw * s)), h = Math.max(1, Math.round(nh * s));
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    var d;
    try { ctx.drawImage(img, 0, 0, w, h); d = ctx.getImageData(0, 0, w, h).data; }
    catch (e) { console.warn('[hw-preview] cannot read product image for frame detection', e); return null; }

    function px(x, y) { var i = (y * w + x) * 4; return [d[i], d[i + 1], d[i + 2]]; }
    function dist(a, b) { var r = a[0] - b[0], g = a[1] - b[1], bl = a[2] - b[2]; return Math.sqrt(r * r + g * g + bl * bl); }
    function avg(list) { var o = [0, 0, 0]; list.forEach(function (p) { o[0] += p[0]; o[1] += p[1]; o[2] += p[2]; }); return o.map(function (v) { return v / list.length; }); }
    function median(a) { a = a.slice().sort(function (x, y) { return x - y; }); return a[a.length >> 1]; }

    var bg = avg([px(2, 2), px(w - 3, 2), px(2, h - 3), px(w - 3, h - 3)]);

    // Walks a line of n pixels; returns [outerIndex, innerIndex] or null.
    function scan(x0, y0, dx, dy, n) {
      function at(i) { return px(x0 + dx * i, y0 + dy * i); }
      var o = 0;
      while (o < n * 0.45 && dist(at(o), bg) < 40) o++;
      if (o >= n * 0.45) return null;
      var frameCol = avg([at(Math.min(o + 2, n - 1)), at(Math.min(o + 3, n - 1)), at(Math.min(o + 4, n - 1))]);
      var minRun = Math.max(4, Math.round(n * 0.03)), run = 0;
      for (var j = o + 3; j < n * 0.5; j++) {
        if (dist(at(j), frameCol) > 60) { if (++run >= minRun) return [o, j - run + 1]; }
        else run = 0;
      }
      return null;
    }

    var fr = [0.3, 0.4, 0.5, 0.6, 0.7];
    function side(lines) {
      var res = lines.filter(Boolean);
      if (res.length < 3) return null;
      return [median(res.map(function (p) { return p[0]; })), median(res.map(function (p) { return p[1]; }))];
    }
    var L = side(fr.map(function (f) { return scan(0, Math.round(f * (h - 1)), 1, 0, w); }));
    var R = side(fr.map(function (f) { return scan(w - 1, Math.round(f * (h - 1)), -1, 0, w); }));
    var T = side(fr.map(function (f) { return scan(Math.round(f * (w - 1)), 0, 0, 1, h); }));
    var B = side(fr.map(function (f) { return scan(Math.round(f * (w - 1)), h - 1, 0, -1, h); }));
    if (!L || !R || !T || !B) return null;

    var out = {
      outer: { l: L[0] / w, r: 1 - R[0] / w, t: T[0] / h, b: 1 - B[0] / h },
      inner: { l: L[1] / w, r: 1 - R[1] / w, t: T[1] / h, b: 1 - B[1] / h }
    };
    var ok = [L[1] - L[0], R[1] - R[0]].every(function (t) { return t / w > 0.01 && t / w < 0.3; }) &&
             [T[1] - T[0], B[1] - B[0]].every(function (t) { return t / h > 0.01 && t / h < 0.3; }) &&
             out.inner.l < out.inner.r && out.inner.t < out.inner.b;
    return ok ? out : null;
  }

  /* ---------- turning sideways writing upright ---------- */

  // Returns a copy of canvas `c` turned clockwise by `q` quarter turns.
  function turnCanvas(c, q) {
    q = ((q % 4) + 4) % 4;
    if (!q) return c;
    var o = document.createElement('canvas');
    o.width = q % 2 ? c.height : c.width;
    o.height = q % 2 ? c.width : c.height;
    var ctx = o.getContext('2d');
    ctx.translate(o.width / 2, o.height / 2);
    ctx.rotate(q * Math.PI / 2);
    ctx.drawImage(c, -c.width / 2, -c.height / 2);
    return o;
  }

  // How "upright" a line of writing looks: more ink above the main body of the
  // letters (t, l, d, b, capitals, i-dots) than below it (g, y, p) means upright.
  function uprightScore(c) {
    var w = c.width, h = c.height, d = c.getContext('2d').getImageData(0, 0, w, h).data;
    var rows = new Float64Array(h), total = 0, max = 0;
    for (var y = 0; y < h; y++) {
      var s = 0;
      for (var x = 0; x < w; x++) s += d[(y * w + x) * 4 + 3];
      rows[y] = s; total += s; if (s > max) max = s;
    }
    if (!total) return 0;
    var top = 0, bot = h - 1;
    while (top < h && rows[top] < max * 0.5) top++;
    while (bot > 0 && rows[bot] < max * 0.5) bot--;
    var above = 0, below = 0;
    for (var i = 0; i < top; i++) above += rows[i];
    for (var j = bot + 1; j < h; j++) below += rows[j];
    return (above - below) / total;
  }

  // Quarter turns (clockwise) needed to stand the writing upright. Writing that is
  // clearly taller than it is wide was photographed sideways; pick the direction
  // that looks most upright. Otherwise leave it alone.
  // Sets autoTurns.confident: false when the writing gives little to go on (e.g. all
  // capitals), so the customer can be asked to check it's the right way up.
  function autoTurns(c) {
    autoTurns.confident = true;
    if (c.height < c.width * 1.15) return 0;
    var s1 = uprightScore(turnCanvas(c, 1)), s3 = uprightScore(turnCanvas(c, 3));
    autoTurns.confident = Math.abs(s1 - s3) >= 0.04;
    return s1 >= s3 ? 1 : 3;
  }

  function hexToRgb(hex) {
    var m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    var n = parseInt(m ? m[1] : 'ffffff', 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  function toBlob(canvas, type, q) {
    return new Promise(function (resolve, reject) {
      try {
        canvas.toBlob(function (b) { b ? resolve(b) : reject(new Error('Export failed')); }, type, q);
      } catch (e) { reject(e); } // SecurityError if the base image is cross-origin without CORS
    });
  }

  /* ---------- widget ---------- */

  function init(root) {
    if (root.__hwInit) return;
    root.__hwInit = true;

    var cfg = {
      cloudName: root.getAttribute('data-cloud-name') || '',
      uploadPreset: root.getAttribute('data-upload-preset') || '',
      requireDesign: root.getAttribute('data-require-design') === 'true',
      formSelector: root.getAttribute('data-form-selector') || '',
      gallerySelector: root.getAttribute('data-gallery-selector') || '',
      placeInLayout: root.getAttribute('data-place-in-layout') === 'true',
      hideGallery: root.getAttribute('data-hide-gallery') === 'true',
      outlineCart: root.getAttribute('data-outline-cart') === 'true',
      fullWidthDetails: root.getAttribute('data-full-width-details') === 'true',
      keepOnFrame: root.getAttribute('data-keep-on-frame') === 'true',
      showFrameGuide: root.getAttribute('data-show-frame-guide') === 'true'
    };
    function q(s) { return root.querySelector(s); }

    var fileInputs = Array.prototype.slice.call(root.querySelectorAll('[data-hw-file]'));
    var stage = q('[data-hw-stage]');
    var inner = q('[data-hw-inner]') || stage;   // the part that zooms; all positions are measured on it
    var zoomBtn = q('[data-hw-zoom]');
    var ZOOM = 2.5, zoom = 1;
    var base = q('[data-hw-base]');
    var overlay = q('[data-hw-overlay]');
    var controls = q('[data-hw-controls]');
    var sens = q('[data-hw-sensitivity]');
    var size = q('[data-hw-size]');
    var rot = q('[data-hw-rotate]');
    var color = q('[data-hw-color]');
    var confirmBtn = q('[data-hw-confirm]');
    var removeBtn = q('[data-hw-remove]');
    var downloadBtn = q('[data-hw-download]');
    var status = q('[data-hw-status]');

    var analysis = null, ink = null, confirmed = false, busy = false;
    var rawInk = null, turns = 0;   // writing as photographed, and quarter turns applied to it
    var turnBtn = q('[data-hw-turn]');
    if (stage) stage.classList.add('hw-stage--empty');   // no handwriting yet
    // scale = size the customer asked for (slider); fit = shrink factor applied so it
    // fits on the frame strip. The size actually shown and saved is scale * fit.
    var baseScale = 0.4;   // starting size for the current upload (fraction of photo width)
    var state = { x: 0.5, y: 0.5, scale: baseScale, rot: 0, fit: 1 };
    var frame = null;
    function effScale() { return state.scale * state.fit; }

    var form = findForm();
    function findForm() {
      if (cfg.formSelector) return document.querySelector(cfg.formSelector);
      // Skip Dawn's hidden instalments ("Pay in 4") form, which also posts to /cart/add
      // and sits above the real product form near the price.
      var forms = Array.prototype.slice.call(document.querySelectorAll('form[action*="/cart/add"]'))
        .filter(function (f) { return !/installment/i.test(f.id + ' ' + f.className); });
      return forms.filter(function (f) { return /^product-form-/.test(f.id); })[0] ||
             forms.filter(function (f) { return f.querySelector('[type="submit"], button[name="add"]'); })[0] ||
             forms[0] || null;
    }

    function setStatus(msg, kind) {
      if (!status) return;
      status.textContent = msg || '';
      status.setAttribute('data-kind', kind || '');
    }

    /* --- follow the selected variant's image --- */
    var variantImages = null;
    var vEl = q('[data-hw-variant-images]');
    if (vEl) { try { variantImages = JSON.parse(vEl.textContent); } catch (e) { console.warn('[hw-preview] bad variant map', e); } }

    function currentVariantId() {
      var input = form && form.querySelector('[name="id"]');
      if (input && input.value) return String(input.value);
      return new URLSearchParams(location.search).get('variant') || '';
    }
    var currentVariant = currentVariantId();

    function checkVariant() {
      var id = currentVariantId();
      if (!id || id === currentVariant) return;
      currentVariant = id;
      var src = variantImages[id];
      if (src && base && base.getAttribute('src') !== src) {
        base.src = src;
        invalidate(); // the saved preview showed the old variant, so ask the customer to confirm again
      }
    }

    if (variantImages && base) {
      // Themes change the hidden variant input in different ways, so react to any
      // change event and also poll cheaply as a fallback.
      document.addEventListener('change', function () { setTimeout(checkVariant, 0); });
      setInterval(checkVariant, 500);
    }

    /* --- upload & process --- */
    function onFileChosen(e) {
      var input = e.currentTarget;
      var f = input.files && input.files[0];
      input.value = ''; // so choosing the same photo again still triggers a change
      if (!f) return;
      if (f.type && !/^image\//.test(f.type)) { setStatus('Please choose a photo (JPG or PNG).', 'error'); return; }
      setStatus('Processing...');
      loadImage(f).then(function (img) {
        // Let the status paint before the heavy loop.
        return new Promise(function (r) { setTimeout(function () { r(analyse(img)); }, 20); });
      }).then(function (a) {
        analysis = a;
        state.rot = 0; state.fit = 1;
        if (rot) rot.value = 0;
        if (controls) controls.hidden = false;
        rawInk = renderInk(analysis, sens ? Number(sens.value) : DEFAULT_SENSITIVITY, color ? color.value : '#ffffff');
        turns = rawInk ? autoTurns(rawInk) : 0;   // stand sideways writing upright
        redraw();
        if (!ink) return;
        placeOnFrame();
        if (turns) setStatus('Your photo was sideways, so we turned it. Upside down? Tap Flip.', 'notice');
        // On phones, bring the preview (now under Confirm design) into view.
        if (stage.parentNode === mobileSlot && ink) {
          setTimeout(function () { stage.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 50);
        }
      }).catch(function (e) { setStatus(e.message + ' If it was taken on an iPhone, try a screenshot of it instead.', 'error'); });
    }
    fileInputs.forEach(function (input) { input.addEventListener('change', onFileChosen); });

    // Centres the writing on the bottom strip of the frame at its starting size:
    // up to 70% of the frame's width, and no taller than the strip.
    function placeOnFrame() {
      state.x = 0.5; state.y = 0.5;
      baseScale = 0.4;   // no frame found: 40% of the photo's width
      if (frame && base && base.naturalWidth) {
        var imgRatio = base.naturalHeight / base.naturalWidth;
        var stripH = frame.outer.b - frame.inner.b;
        state.x = (frame.outer.l + frame.outer.r) / 2;
        state.y = (frame.inner.b + frame.outer.b) / 2;
        baseScale = Math.min((frame.outer.r - frame.outer.l) * 0.7,
                             stripH * 0.85 * imgRatio * ink.width / ink.height);
      }
      // The Size slider's middle is this starting size; see sliderToScale().
      if (size) size.value = 50;
      state.scale = baseScale;
      place();
    }

    // "Turn 90 degrees" button: turns the writing itself a quarter turn clockwise, then
    // re-fits it to the frame (a sideways line needs a very different size).
    function turnBy(q) {
      if (!rawInk) return;
      turns = (turns + q) % 4;
      redraw();
      // A flip keeps the same shape, so keep the customer's size and position;
      // a quarter turn changes the shape, so re-fit it to the frame.
      if (ink && q % 2) placeOnFrame();
    }
    if (turnBtn) turnBtn.addEventListener('click', function () { turnBy(1); });
    // "Flip": half a turn, for writing that has come out upside down.
    var flipBtn = q('[data-hw-flip]');
    if (flipBtn) flipBtn.addEventListener('click', function () { turnBy(2); });

    function redraw() {
      if (!analysis) return;
      ink = rawInk ? turnCanvas(rawInk, turns) : null;
      invalidate();
      if (!ink) {
        overlay.hidden = true;
        stage.classList.add('hw-stage--empty');
        setStatus('No handwriting detected. Try a clearer photo: dark pen on plain white paper, in good light.', 'error');
        return;
      }
      overlay.width = ink.width; overlay.height = ink.height;
      var ctx = overlay.getContext('2d');
      ctx.clearRect(0, 0, ink.width, ink.height);
      ctx.drawImage(ink, 0, 0);
      overlay.hidden = false;
      stage.classList.remove('hw-stage--empty');
      showZoomButton(true);
      place();
      setStatus(cfg.requireDesign ? 'Drag to position, then press Confirm design.' : 'Drag to position your handwriting.');
    }

    function place() {
      constrain();
      overlay.style.left = (state.x * 100) + '%';
      overlay.style.top = (state.y * 100) + '%';
      overlay.style.width = (effScale() * 100) + '%';
      overlay.style.transform = 'translate(-50%, -50%) rotate(' + state.rot + 'deg)';
      if (!drag) applyZoom();
    }

    /* --- keep the writing on the frame --- */
    // Picks the frame strip (bottom, top, left, right) the writing can sit on with the
    // least movement, slides it fully onto that strip, and shrinks it if it's too big.
    function constrain() {
      state.fit = 1;
      if (!cfg.keepOnFrame || !frame || !ink) return;
      var r = inner.getBoundingClientRect(), W = r.width, H = r.height;
      if (!W || !H) return;
      var O = { l: frame.outer.l * W, r: frame.outer.r * W, t: frame.outer.t * H, b: frame.outer.b * H };
      var I = { l: frame.inner.l * W, r: frame.inner.r * W, t: frame.inner.t * H, b: frame.inner.b * H };
      var strips = [
        { l: O.l, r: O.r, t: I.b, b: O.b },  // bottom
        { l: O.l, r: O.r, t: O.t, b: I.t },  // top
        { l: O.l, r: I.l, t: O.t, b: O.b },  // left
        { l: I.r, r: O.r, t: O.t, b: O.b }   // right
      ];
      var a = state.rot * Math.PI / 180, cs = Math.abs(Math.cos(a)), sn = Math.abs(Math.sin(a));
      var w = state.scale * W, h = w * ink.height / ink.width;
      var bw = w * cs + h * sn, bh = w * sn + h * cs;          // rotated bounding box
      var cx = state.x * W, cy = state.y * H, best = null;
      strips.forEach(function (st) {
        var sw = st.r - st.l, sh = st.b - st.t;
        if (sw <= 0 || sh <= 0) return;
        var f = Math.min(1, sw / bw, sh / bh) * (Math.min(sw / bw, sh / bh) < 1 ? 0.96 : 1);
        var hw = bw * f / 2, hh = bh * f / 2;
        var nx = Math.min(st.r - hw, Math.max(st.l + hw, cx));
        var ny = Math.min(st.b - hh, Math.max(st.t + hh, cy));
        var score = Math.hypot(nx - cx, ny - cy) + (1 - f) * (W + H);   // prefer no shrinking
        if (!best || score < best.score) best = { score: score, x: nx, y: ny, f: f };
      });
      if (!best) return;
      state.x = best.x / W; state.y = best.y / H; state.fit = best.f;
    }

    // Detect the frame whenever the product image (re)loads, e.g. on variant change.
    var guide = null;
    function onBaseLoad() {
      // Always detected: used to start new uploads on the bottom of the frame, and
      // (only when "Keep writing on the frame" is on) to stop it leaving the frame.
      frame = detectFrame(base);
      if (!frame) console.warn('[hw-preview] Frame not detected in this image; writing starts in the centre.');
      drawGuide();
      if (ink) place();
    }
    function drawGuide() {
      if (guide) { guide.remove(); guide = null; }
      if (!cfg.showFrameGuide || !frame) return;
      guide = document.createElement('div');
      guide.className = 'hw-frame-guide';
      ['outer', 'inner'].forEach(function (k) {
        var b = document.createElement('div'), f = frame[k];
        b.style.left = f.l * 100 + '%'; b.style.top = f.t * 100 + '%';
        b.style.width = (f.r - f.l) * 100 + '%'; b.style.height = (f.b - f.t) * 100 + '%';
        guide.appendChild(b);
      });
      inner.insertBefore(guide, overlay);
    }
    if (base) {
      base.addEventListener('load', onBaseLoad);
      if (base.complete && base.naturalWidth) onBaseLoad();
    }
    window.addEventListener('resize', function () { if (ink) place(); });

    if (sens) sens.addEventListener('input', redraw);
    if (color) color.addEventListener('input', redraw);
    // Size slider (0-100) is relative to the starting size: 50 = starting size,
    // 0 = a quarter of it, 100 = four times it. Each step is about 3%, so it can be
    // fine-tuned on a phone, where the frame strip is only a few pixels tall.
    function sliderToScale(v) { return Math.min(1, baseScale * Math.pow(2, (v - 50) / 25)); }
    if (size) size.addEventListener('input', function () { state.scale = sliderToScale(Number(size.value)); place(); invalidate(); });
    if (rot) rot.addEventListener('input', function () { state.rot = Number(rot.value); place(); invalidate(); });

    /* --- drag to position (mouse + touch) --- */
    var drag = null;
    overlay.addEventListener('pointerdown', function (e) {
      var r = inner.getBoundingClientRect();
      drag = { id: e.pointerId, dx: e.clientX - (r.left + state.x * r.width), dy: e.clientY - (r.top + state.y * r.height) };
      overlay.setPointerCapture(e.pointerId);
      overlay.classList.add('is-dragging');
      e.preventDefault();
    });
    overlay.addEventListener('pointermove', function (e) {
      if (!drag || drag.id !== e.pointerId) return;
      var r = inner.getBoundingClientRect();
      state.x = Math.min(1, Math.max(0, (e.clientX - drag.dx - r.left) / r.width));
      state.y = Math.min(1, Math.max(0, (e.clientY - drag.dy - r.top) / r.height));
      place();
    });
    function endDrag(e) {
      if (drag && drag.id === e.pointerId) { drag = null; overlay.classList.remove('is-dragging'); applyZoom(); invalidate(); }
    }
    overlay.addEventListener('pointerup', endDrag);
    overlay.addEventListener('pointercancel', endDrag);

    /* --- zoom (mainly for phones, where the frame strip is only a few pixels tall) --- */
    // Scales the image up and centres the view on the writing, keeping the image
    // covering the box. Not re-centred mid-drag, so the view doesn't slide under a finger.
    function applyZoom() {
      if (inner === stage) return;
      if (zoom === 1 || !ink) { inner.style.transform = ''; return; }
      var Wv = stage.clientWidth, Hv = stage.clientHeight;
      var W = Wv * zoom, H = Hv * zoom;
      var tx = Math.min(0, Math.max(Wv - W, Wv / 2 - state.x * W));
      var ty = Math.min(0, Math.max(Hv - H, Hv / 2 - state.y * H));
      inner.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + zoom + ')';
    }
    function setZoom(z) {
      zoom = z;
      if (zoomBtn) {
        zoomBtn.textContent = z > 1 ? 'Zoom out' : 'Zoom in';
        zoomBtn.setAttribute('aria-pressed', z > 1 ? 'true' : 'false');
      }
      applyZoom();
    }
    function showZoomButton(on) { if (zoomBtn) zoomBtn.hidden = !on; if (!on) setZoom(1); }
    if (zoomBtn) zoomBtn.addEventListener('click', function () { setZoom(zoom > 1 ? 1 : ZOOM); });

    /* --- remove --- */
    if (removeBtn) removeBtn.addEventListener('click', function () {
      analysis = null; ink = null; rawInk = null; turns = 0;
      fileInputs.forEach(function (input) { input.value = ''; });
      overlay.hidden = true;
      stage.classList.add('hw-stage--empty');
      showZoomButton(false);
      if (controls) controls.hidden = true;
      invalidate();
      setStatus('');
    });

    /* --- exports --- */
    function composite() {
      var bw = base.naturalWidth, bh = base.naturalHeight;
      var c = document.createElement('canvas');
      c.width = bw; c.height = bh;
      var ctx = c.getContext('2d');
      ctx.drawImage(base, 0, 0, bw, bh);
      var w = effScale() * bw, h = w * ink.height / ink.width;
      ctx.translate(state.x * bw, state.y * bh);
      ctx.rotate(state.rot * Math.PI / 180);
      ctx.drawImage(ink, -w / 2, -h / 2, w, h);
      return c;
    }

    function upload(blob, name) {
      var fd = new FormData();
      fd.append('file', blob, name);
      fd.append('upload_preset', cfg.uploadPreset);
      return fetch('https://api.cloudinary.com/v1_1/' + encodeURIComponent(cfg.cloudName) + '/image/upload', { method: 'POST', body: fd })
        .then(function (r) { return r.json().then(function (j) {
          if (!r.ok) throw new Error((j.error && j.error.message) || 'Upload failed');
          // Safety net: if Cloudinary says the file already existed, it did not store this
          // upload, so the link would point at someone else's design. Refuse it.
          if (j.existing) throw new Error('Upload was not saved as a new file. Please try again.');
          return j.secure_url;
        }); });
    }

    if (downloadBtn) downloadBtn.addEventListener('click', function () {
      if (!ink) return;
      toBlob(ink, 'image/png').then(function (b) {
        var a = document.createElement('a');
        a.href = URL.createObjectURL(b); a.download = 'handwriting.png'; a.click();
        setTimeout(function () { URL.revokeObjectURL(a.href); }, 1000);
      });
    });

    if (confirmBtn) confirmBtn.addEventListener('click', function () {
      if (!ink || busy) return;
      if (!cfg.cloudName || !cfg.uploadPreset) {
        setStatus('Upload storage is not configured (Cloudinary cloud name / upload preset).', 'error');
        return;
      }
      busy = true; confirmBtn.disabled = true;
      setStatus('Saving your design...');
      var inkUrl;
      // Every design gets its own file names. Cloudinary never overwrites on unsigned uploads,
      // so a repeated name (e.g. "handwriting.png") would hand back the FIRST file ever uploaded.
      // The handwriting and its preview share the same id so they are easy to pair up.
      var designId = 'hw-' + new Date().toISOString().slice(0, 10).replace(/-/g, '') + '-' +
                     Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      toBlob(ink, 'image/png')
        .then(function (b) { return upload(b, designId + '-handwriting.png'); })
        .then(function (u) {
          inkUrl = u;
          // The preview is a nice-to-have; if the product image is not CORS-enabled the canvas
          // is "tainted" and cannot be exported, so we continue without it.
          return toBlob(composite(), 'image/jpeg', 0.9)
            .then(function (b) { return upload(b, designId + '-preview.jpg'); })
            .catch(function (e) { console.warn('[hw-preview] preview export skipped:', e); return ''; });
        })
        .then(function (previewUrl) {
          setProps({
            // What the customer sees in the cart, checkout and emails.
            'Handwriting': 'Added',
            // Names starting with "_" are hidden from customers but shown on the order in admin.
            '_Handwriting file': inkUrl,
            // Same file shown as black writing on white: white writing on a transparent
            // background looks blank in a browser. Cloudinary recolours it when the link is opened;
            // the stored file itself is unchanged.
            '_Handwriting (easy view)': inkUrl.replace('/image/upload/', '/image/upload/e_colorize:100,co_rgb:000000/b_white/'),
            '_Preview': previewUrl,
            '_Ink colour': color ? color.value : '#ffffff',
            '_Placement': JSON.stringify({
              x: +state.x.toFixed(4), y: +state.y.toFixed(4),
              widthFraction: +effScale().toFixed(4), rotationDeg: state.rot
            })
          });
          confirmed = true;
          setStatus('Design saved. You can now add to cart.', 'ok');
        })
        .catch(function (e) { setStatus('Could not save your design: ' + e.message, 'error'); })
        .then(function () { busy = false; confirmBtn.disabled = false; });
    });

    /* --- cart integration --- */
    function setProps(map) {
      clearProps();
      if (!form) return;
      Object.keys(map).forEach(function (k) {
        if (!map[k]) return;
        var i = document.createElement('input');
        i.type = 'hidden'; i.name = 'properties[' + k + ']'; i.value = map[k];
        i.setAttribute('data-hw-prop', '');
        form.appendChild(i);
      });
    }
    function clearProps() {
      if (!form) return;
      form.querySelectorAll('[data-hw-prop]').forEach(function (el) { el.remove(); });
    }
    function invalidate() {
      if (confirmed) setStatus('Design changed - press Confirm design again to save it.');
      confirmed = false;
      clearProps();
    }

    // Capture phase on document runs before the theme's own submit handler,
    // so we can stop an add-to-cart that has no confirmed design.
    document.addEventListener('submit', function (e) {
      if (!form || e.target !== form) return;
      var needsConfirm = ink && !confirmed;              // they started a design but didn't save it
      var missing = cfg.requireDesign && !confirmed;     // product requires a design
      if (needsConfirm || missing) {
        e.preventDefault();
        e.stopImmediatePropagation();
        setStatus(ink ? 'Please press Confirm design before adding to cart.' : 'Please upload your handwriting first.', 'error');
        (panel || root).scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, true);

    /* --- move into the theme's product layout --- */
    // Runs last: every element above was looked up inside `root` before anything moves.
    var panel = q('[data-hw-panel]');
    var example = q('[data-hw-example]');
    var mobileSlot = q('[data-hw-mobile-slot]');
    // Invisible marker for where the preview lives on desktop (moved with it below).
    var stageHome = document.createElement('span');
    stageHome.hidden = true;
    stage.parentNode.insertBefore(stageHome, stage.nextSibling);

    if (cfg.placeInLayout) placeInLayout();

    /* --- phones: editable preview under Confirm design; desktop: under the example --- */
    // 749px matches Dawn's mobile breakpoint.
    var mobileMQ = window.matchMedia('(max-width: 749px)');
    function positionStage() {
      if (mobileMQ.matches && mobileSlot) {
        if (stage.parentNode !== mobileSlot) mobileSlot.appendChild(stage);
      } else if (stageHome.parentNode && stage.nextSibling !== stageHome) {
        stageHome.parentNode.insertBefore(stage, stageHome);
      }
      if (ink) place();
    }
    if (example) {   // only split the images when there is an example to show at the top
      positionStage();
      if (mobileMQ.addEventListener) mobileMQ.addEventListener('change', positionStage);
      else mobileMQ.addListener(positionStage);
    }

    // Dawn swaps Add to cart to a filled button when dynamic checkout is off.
    // Re-apply its own outline class (checked periodically, as some theme versions
    // re-render the button when the variant changes).
    if (cfg.outlineCart) {
      var outline = function () {
        var f = findForm();
        var btn = f && f.querySelector('.product-form__submit, button[name="add"]');
        if (btn && !btn.classList.contains('button--secondary')) btn.classList.add('button--secondary');
      };
      outline();
      setInterval(outline, 1000);
    }

    function placeInLayout() {
      var sid = root.getAttribute('data-section-id') || 'hw';
      // Theme editor reloads: remove pieces a previous copy of this section moved out.
      // (.hw-details holds the theme's own description blocks, so it is never removed.)
      document.querySelectorAll('[data-hw-moved="' + sid + '"]:not(.hw-details)').forEach(function (el) {
        if (!root.contains(el)) el.remove();
      });

      var gallery = cfg.gallerySelector ? document.querySelector(cfg.gallerySelector) : firstMatch([
        '.product__media-wrapper',      // Dawn and most free Shopify themes
        'media-gallery',
        '.product__media-gallery',
        '.product-gallery',
        '.product-single__media-group',
        '.product__images'
      ]);
      var anchor = form ? (form.closest('product-form') || form) : null;

      if (gallery && stage) {
        var first = gallery.firstChild;
        if (example) {
          example.setAttribute('data-hw-moved', sid);
          example.classList.add('hw-stage--in-gallery');
          gallery.insertBefore(example, first);
        }
        stage.setAttribute('data-hw-moved', sid);
        stage.classList.add('hw-stage--in-gallery');
        gallery.insertBefore(stage, first);
        stageHome.setAttribute('data-hw-moved', sid);
        gallery.insertBefore(stageHome, first);   // desktop spot for the preview: under the example
        if (cfg.hideGallery) gallery.classList.add('hw-gallery-replaced');
      } else {
        console.warn('[hw-preview] Product image area not found; preview left where the section is.');
      }

      if (anchor && panel && anchor.parentNode) {
        panel.setAttribute('data-hw-moved', sid);
        panel.classList.add('hw-panel--in-info');
        anchor.parentNode.insertBefore(panel, anchor);
      } else {
        console.warn('[hw-preview] Add to cart form not found; controls left where the section is.');
      }

      // Description + collapsible rows: move from the buy column to full width below
      // the image/buy columns, so the short image column doesn't leave a big gap.
      if (cfg.fullWidthDetails && gallery && !document.querySelector('.hw-details[data-hw-moved="' + sid + '"]')) {
        var grid = gallery.closest('.product') || gallery.parentNode;
        var info = (anchor && anchor.closest('.product__info-container, .product__info-wrapper')) ||
                   document.querySelector('.product__info-container');
        var items = info ? info.querySelectorAll('.product__description, .product__accordion') : [];
        if (grid && grid.parentNode && items.length) {
          var details = document.createElement('div');
          details.className = 'hw-details';
          details.setAttribute('data-hw-moved', sid);
          Array.prototype.forEach.call(items, function (el) { details.appendChild(el); });
          grid.parentNode.insertBefore(details, grid.nextSibling);
        } else if (!items.length) {
          console.warn('[hw-preview] No description / collapsible rows found to move.');
        }
      }

      // Collapse the now-empty section so it doesn't leave a gap further down the page.
      if (!root.contains(stageHome) && !root.contains(panel)) root.classList.add('hw-preview--moved');
    }

    function firstMatch(selectors) {
      for (var i = 0; i < selectors.length; i++) {
        var el = document.querySelector(selectors[i]);
        if (el) return el;
      }
      return null;
    }
  }

  function initAll() { document.querySelectorAll('[data-hw-preview]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  // Re-init when the section is added/reloaded in the theme editor.
  document.addEventListener('shopify:section:load', initAll);
})();